<template>
	<div
		class="grid col-span-4 border-b grid-cols-subgrid cursor-pointer"
		:class="{
			'opacity-50': documentStatus?.status === '08 Vervallen (zie toelichting)',
		}"
	>
		<div class="h-full p-0 whitespace-nowrap">
			<a class="flex h-full p-2 pl-6" @click="emit('selected', doc)">
				<div class="flex items-center">
					<AppIcon :name="iconName" :class="iconColor" class="text-2xl" />
				</div>
			</a>
		</div>
		<div class="h-full p-0 overflow-hidden whitespace-nowrap">
			<a class="flex h-full p-2 pr-6" @click="emit('selected', doc)">
				<div class="flex flex-col overflow-hidden">
					<div class="flex items-center gap-2">
						<span class="font-medium">
							{{ splitHdnValue(doc.documentType)[1] }}
						</span>
						<span
							v-if="classification"
							class="shrink-0 px-2 py-0.5 rounded text-xs"
							:class="
								classification.confidence >= CLASSIFICATIE_HOGE_CONFIDENCE
									? 'classification-high'
									: classification.confidence >= CLASSIFICATIE_MIN_CONFIDENCE
										? 'classification-medium'
										: 'classification-unknown'
							"
							:title="`${Math.round(classification.confidence * 100)}% zekerheid`"
						>
							{{
								classification.confidence >= CLASSIFICATIE_MIN_CONFIDENCE
									? classification.label
									: CLASSIFICATIE_ONBEKEND_LABEL
							}}
						</span>
						<span
							v-if="pendingBadgeText"
							class="shrink-0 px-2 py-0.5 rounded text-xs pending-classification"
						>
							{{ pendingBadgeText }}
						</span>
					</div>
					<span
						v-if="doc.documentTypeDescription"
						class="text-sm text-gray-500 truncate"
					>
						{{ doc.documentTypeDescription }}
					</span>
				</div>
			</a>
		</div>
		<div class="h-full p-0">
			<a class="flex w-full h-full" @click="emit('selected', doc)">
				<div
					v-if="doc.partijNawData"
					class="flex items-center h-full gap-1 p-2"
				>
					<span class="text-2xl ht-icon person-icon"> person </span>
					<span class="text-sm font-medium">
						{{
							doc.partijNawData.correspondenceFirstname ??
							doc.partijNawData.initials ??
							doc.partijNawData.givenNames
						}}
						{{
							doc.partijNawData.correspondenceInsert ??
							doc.partijNawData.birthInsert
						}}
						{{
							doc.partijNawData.correspondenceSurname ??
							doc.partijNawData.birthSurname
						}}
					</span>
				</div>
			</a>
		</div>
		<div class="h-full p-0 whitespace-nowrap">
			<a
				class="flex items-center justify-end h-full gap-2 p-2 pr-6"
				@click="emit('selected', doc)"
			>
				<TransitionGroup name="icon-list" tag="div" class="flex items-center">
					<Transition key="sent">
						<span
							v-show="
								doc.sentDocumentAction?.documentId &&
								doc.sentDocumentAction?.documentId ===
									doc.linkedDocumentAction?.documentId
							"
							class="text-2xl text-gray-500 duration-100 ht-icon text-medium"
						>
							send
						</span>
					</Transition>
					<Transition key="linked">
						<span
							v-show="doc.linkedDocumentAction?.documentId"
							class="text-2xl text-gray-500 duration-100 ht-icon text-medium"
						>
							link
						</span>
					</Transition>
				</TransitionGroup>
				<span v-if="documentStatus?.createdAt" class="text-sm text-gray-500">
					{{ formatDate(new Date(documentStatus.createdAt)) }}
				</span>
			</a>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { splitHdnValue } from "@/helpers/hdnHelpers";
import { formatDate } from "@/helpers/uiHelpers";
import { useLoanApplicationDocumentIcon } from "@/composables/useLoanApplicationDocumentIcon";
import { useNow } from "@vueuse/core";
import { computed, inject, ref, type Ref } from "vue";
import {
	getStatus,
	type HdnRequestedDocument,
} from "@/composables/useLoanApplicationSortedDocuments";
import {
	CLASSIFICATIE_MIN_CONFIDENCE,
	CLASSIFICATIE_HOGE_CONFIDENCE,
	CLASSIFICATIE_ONBEKEND_LABEL,
} from "@/parameters/documentClassificatie";
import { getPendingClassificationStatus } from "@/lib/documentClassificatieStatus";

const emit = defineEmits<{ selected: [doc: HdnRequestedDocument] }>();
const props = defineProps<{ doc: HdnRequestedDocument }>();

const documentStatus = computed(() => {
	const selectedDocument = props.doc;
	if (!selectedDocument) {
		return;
	}

	return getStatus(selectedDocument);
});
const { iconName, iconColor } = useLoanApplicationDocumentIcon(
	() => documentStatus.value?.status,
);

const classificationsMap =
	inject<Ref<Map<string, { label: string; confidence: number }>>>(
		"classificationsMap",
	);

const classificationFeatureEnabled = inject<Ref<boolean>>(
	"classificationFeatureEnabled",
	ref(false),
);

const classification = computed(() => {
	const docId = props.doc.linkedDocumentAction?.documentId;
	if (!docId || !classificationsMap?.value) return null;
	return classificationsMap.value.get(docId) ?? null;
});

// Reactieve tijdbron zodat de pending-computed herberekent na PENDING_WINDOW_MS
// (1 min interval is ruim genoeg voor de 5-min-grens).
const now = useNow({ interval: 60_000 });

const pendingStatus = computed(() => {
	if (classification.value) return null;
	const doc = props.doc.linkedDocumentAction?.document;
	if (!doc) return null;
	return getPendingClassificationStatus(doc, {
		featureFlagEnabled: classificationFeatureEnabled.value === true,
		now: now.value.getTime(),
	});
});

const pendingBadgeText = computed(() => {
	switch (pendingStatus.value) {
		case "pending":
			return "Wordt geclassificeerd";
		case "not-classifiable":
			return "Niet classificeerbaar";
		default:
			return null;
	}
});
</script>

<style lang="css" scoped>
.v-enter-active,
.v-leave-active {
	transition: all 0.1s ease;
}

.v-enter-from,
.v-leave-to {
	transform: translateX(6px);
	opacity: 0;
}

.person-icon {
	color: var(--text-secondary);
}

.icon-list-enter-active,
.icon-list-leave-active {
	transition: all 0.1s ease;
}

.icon-list-enter-from,
.icon-list-leave-to {
	opacity: 0;
	transform: translateX(100%);
}

.pending-classification {
	background: var(--background-secondary);
	color: var(--text-secondary);
	font-style: italic;
}

.classification-high {
	background: color-mix(in srgb, var(--color-success) 15%, white);
	color: var(--color-success);
}

.classification-medium {
	background: var(--background-secondary);
	color: var(--text-secondary);
}

.classification-unknown {
	background: color-mix(in srgb, var(--color-warn) 15%, white);
	color: var(--color-warn);
	font-style: italic;
}
</style>
